import React, { useState, useEffect, useCallback } from 'react';
import { Card, Btn, Input, Select, Field, Pagination, usePagination } from '../../components/UI';
import NrpLookup from '../../components/NrpLookup';
import { useToast } from '../../components/Toast';
import { tahorAPI } from '../../utils/api';
import ApprovalHistoryModal from '../../components/ApprovalHistoryModal';
import { exportCSV, printTable } from '../../utils/exportHelper';

const StatusBadge = ({ status }) => {
  const colors = {
    DRAFT: { bg: '#F3F4F6', color: '#374151' },
    FINAL: { bg: '#E0E7FF', color: '#3730A3' },
    TERPOSTING: { bg: '#DCFCE7', color: '#166534' },
    DITOLAK: { bg: '#FEE2E2', color: '#991B1B' },
    GAGAL: { bg: '#FEE2E2', color: '#991B1B' },
  };
  const c = colors[status] || { bg: '#F3F4F6', color: '#374151' };
  return <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{status}</span>;
};

const th = { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', borderBottom: '2px solid #E2E8F0' };
const td = { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #F1F5F9' };
const rowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 };

const Pager = ({ page, total, limit, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, justifyContent: 'center' }}>
      <Btn onClick={() => onChange(page - 1)} disabled={page <= 1} variant="secondary">← Prev</Btn>
      <span style={{ fontSize: 12, color: '#6B7280', padding: '0 8px' }}>Hal {page} / {totalPages}</span>
      <Btn onClick={() => onChange(page + 1)} disabled={page >= totalPages} variant="secondary">Next →</Btn>
    </div>
  );
};

// ── MASTER TAB ────────────────────────────────────────────
const MasterTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const [form, setForm] = useState({ nama: '', singkatan: '', kategori: '', tingkat: '', deskripsi: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await tahorAPI.getMaster(); if (r.success) setData(r.data); }
    catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.nama || !form.kategori) { toast('Nama dan kategori harus diisi', 'error'); return; }
    try {
      const r = await tahorAPI.createMaster(form);
      if (r.success) { toast('Tanda kehormatan berhasil ditambahkan'); setForm({ nama: '', singkatan: '', kategori: '', tingkat: '', deskripsi: '' }); load(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Hapus tanda kehormatan ini?')) return;
    try { const r = await tahorAPI.deleteMaster(id); if (r.success) { toast('Dihapus'); load(); } }
    catch (err) { toast(err.message, 'error'); }
  };

  const grouped = data.reduce((acc, item) => { (acc[item.kategori] = acc[item.kategori] || []).push(item); return acc; }, {});

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Tambah Tanda Kehormatan Baru</h3>
        <div style={rowStyle}>
          <Field label="Nama Lengkap"><Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Contoh: Bintang Kartika Eka Paksi" /></Field>
          <Field label="Singkatan"><Input value={form.singkatan} onChange={(e) => setForm({ ...form, singkatan: e.target.value })} placeholder="Contoh: BKEP" /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Kategori"><Select value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'bintang', label: 'Bintang' }, { value: 'monel', label: 'Monel / Satyalancana' }, { value: 'piagam', label: 'Piagam' }, { value: 'bintang_persen', label: 'Bintang Persen' }, { value: 'lengan', label: 'Lengan' }]} /></Field>
          <Field label="Tingkat (1/2/3)"><Input type="number" value={form.tingkat} onChange={(e) => setForm({ ...form, tingkat: e.target.value })} placeholder="1, 2, atau 3" /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Deskripsi"><Input value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} /></Field>
        </div>
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan'}</Btn>
      </Card>
      {Object.entries(grouped).map(([kategori, items]) => (
        <Card key={kategori}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, textTransform: 'capitalize' }}>{kategori} ({items.length})</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>Nama</th><th style={th}>Singkatan</th><th style={th}>Tingkat</th><th style={th}>Deskripsi</th><th style={{ ...th, textAlign: 'right' }}></th>
            </tr></thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{item.nama}</td><td style={td}>{item.singkatan}</td><td style={td}>{item.tingkat}</td>
                  <td style={{ ...td, fontSize: 12, color: '#64748B' }}>{item.deskripsi}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <Btn onClick={() => handleDelete(item.id)} variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}>Hapus</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </>
  );
};

// ── RIWAYAT TAB ────────────────────────────────────────────
const RiwayatTab = () => {
  const [data, setData] = useState([]);
  const [master, setMaster] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const { toast } = useToast();
  const [filters, setFilters] = useState({ nrp: '', status: '', kategori: '' });
  const [form, setForm] = useState({ nrp: '', tanda_kehormatan_id: '', no_persetujuan: '', tgl_persetujuan: '', no_register: '', tgl_register: '', no_sk: '', tgl_sk: '', keterangan: '' });
  const [selPersonel, setSelPersonel] = useState(null);

  const loadMaster = useCallback(async () => {
    try { const r = await tahorAPI.getMaster(); if (r.success) setMaster(r.data); } catch {}
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `?page=${page}&limit=15${filters.nrp ? `&nrp=${filters.nrp}` : ''}${filters.status ? `&status=${filters.status}` : ''}${filters.kategori ? `&kategori=${filters.kategori}` : ''}`;
      const r = await tahorAPI.getRiwayat(qs);
      if (r.success) { setData(r.data); setTotal(r.pagination?.total || 0); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, filters, toast]);

  useEffect(() => { loadMaster(); }, [loadMaster]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async () => {
    if (!form.nrp || !form.tanda_kehormatan_id) { toast('NRP dan Tanda Kehormatan harus diisi', 'error'); return; }
    try {
      const r = await tahorAPI.createRiwayat(form);
      if (r.success) { toast('Riwayat berhasil disimpan'); setForm({ nrp: '', tanda_kehormatan_id: '', no_persetujuan: '', tgl_persetujuan: '', no_register: '', tgl_register: '', no_sk: '', tgl_sk: '', keterangan: '' }); setSelPersonel(null); loadData(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  const handlePosting = async (id) => {
    try { const r = await tahorAPI.postingRiwayat(id); if (r.success) { toast('Berhasil diposting'); loadData(); } }
    catch (err) { toast(err.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Hapus riwayat ini?')) return;
    try { const r = await tahorAPI.deleteRiwayat(id); if (r.success) { toast('Dihapus'); loadData(); } }
    catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Tambah Riwayat Tanda Kehormatan</h3>
        <div style={rowStyle}>
          <Field label="NRP Personel" required><NrpLookup value={selPersonel} onSelect={(p) => { setSelPersonel(p); setForm(f => ({...f, nrp: p.nrp})); }} onClear={() => { setSelPersonel(null); setForm(f => ({...f, nrp: ''})); }} /></Field>
          <Field label="Tanda Kehormatan"><Select value={form.tanda_kehormatan_id} onChange={(e) => setForm({ ...form, tanda_kehormatan_id: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, ...master.map(m => ({ value: m.id, label: `${m.nama} (${m.singkatan})` }))]} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="No. Persetujuan"><Input value={form.no_persetujuan} onChange={(e) => setForm({ ...form, no_persetujuan: e.target.value })} /></Field>
          <Field label="Tgl. Persetujuan"><Input type="date" value={form.tgl_persetujuan} onChange={(e) => setForm({ ...form, tgl_persetujuan: e.target.value })} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="No. Register"><Input value={form.no_register} onChange={(e) => setForm({ ...form, no_register: e.target.value })} /></Field>
          <Field label="Tgl. Register"><Input type="date" value={form.tgl_register} onChange={(e) => setForm({ ...form, tgl_register: e.target.value })} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="No. SK"><Input value={form.no_sk} onChange={(e) => setForm({ ...form, no_sk: e.target.value })} /></Field>
          <Field label="Tgl. SK"><Input type="date" value={form.tgl_sk} onChange={(e) => setForm({ ...form, tgl_sk: e.target.value })} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Keterangan"><Input value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></Field>
        </div>
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Filter</h3>
        <div style={rowStyle}>
          <Field label="Cari NRP"><Input value={filters.nrp} onChange={(e) => { setFilters({ ...filters, nrp: e.target.value }); setPage(1); }} placeholder="Cari NRP..." /></Field>
          <Field label="Status"><Select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
            options={[{ value: '', label: 'Semua Status' }, { value: 'DRAFT', label: 'DRAFT' }, { value: 'FINAL', label: 'FINAL' }, { value: 'TERPOSTING', label: 'TERPOSTING' }]} /></Field>
          <Field label="Kategori"><Select value={filters.kategori} onChange={(e) => { setFilters({ ...filters, kategori: e.target.value }); setPage(1); }}
            options={[{ value: '', label: 'Semua Kategori' }, { value: 'bintang', label: 'Bintang' }, { value: 'monel', label: 'Monel/Satyalancana' }, { value: 'piagam', label: 'Piagam' }]} /></Field>
        </div>
      </Card>
      <Card>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin:0 }}>Daftar Riwayat Tanda Kehormatan ({total})</h3>
          <div style={{ display:'flex',gap:6 }}>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['NRP','Nama','Tanda Kehormatan','No. Persetujuan','Tgl. Persetujuan','Status']; exportCSV('tahor_riwayat', h, data.map(r=>[r.nrp,r.nama,r.nama_tanda,r.no_persetujuan,r.tgl_persetujuan,r.status])); }}>CSV</Btn>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['NRP','Nama','Tanda Kehormatan','No. Persetujuan','Tgl. Persetujuan','Status']; printTable('Riwayat Tanda Kehormatan', h, data.map(r=>[r.nrp,r.nama,r.nama_tanda,r.no_persetujuan,r.tgl_persetujuan,r.status])); }}>Print</Btn>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>NRP</th><th style={th}>Nama</th><th style={th}>Tanda Kehormatan</th>
              <th style={th}>No. Persetujuan</th><th style={th}>Tgl. Persetujuan</th><th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }}>Aksi</th>
            </tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.nrp}</td>
                  <td style={td}>{r.nama}<br /><span style={{ fontSize: 11, color: '#64748B' }}>{r.pangkat}</span></td>
                  <td style={td}>{r.nama_tanda}<br /><span style={{ fontSize: 11, color: '#64748B' }}>{r.singkatan} {r.tingkat ? `Tingkat ${r.tingkat}` : ''}</span></td>
                  <td style={td}>{r.no_persetujuan}</td>
                  <td style={{ ...td, fontSize: 12 }}>{r.tgl_persetujuan}</td>
                  <td style={td}><StatusBadge status={r.status} /></td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div style={{ display:'flex', gap:4, justifyContent:'flex-end', flexWrap:'wrap' }}>
                      {r.status === 'DRAFT' && <>
                        <Btn onClick={() => handlePosting(r.id)} variant="success" style={{ padding: '4px 10px', fontSize: 12 }}>Posting</Btn>
                        <Btn onClick={async () => {
                          const catatan = window.prompt('Alasan penolakan:');
                          if (catatan === null) return;
                          try { await tahorAPI.rejectRiwayat(r.id, catatan); toast('Riwayat ditolak'); loadData(); } catch(e) { toast(e.message, 'error'); }
                        }} variant="warning" style={{ padding: '4px 10px', fontSize: 12 }}>Tolak</Btn>
                      </>}
                      {r.status !== 'TERPOSTING' && <Btn onClick={() => handleDelete(r.id)} variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}>Hapus</Btn>}
                      <Btn onClick={() => setHistoryModal(r.id)} variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}>Riwayat</Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={15} onChange={setPage} />
      </Card>
      {historyModal && <ApprovalHistoryModal module="tahor" referenceId={historyModal} onClose={() => setHistoryModal(null)} />}
    </>
  );
};

// ── UPLOAD TAHOR TAB ──────────────────────────────────────
const UploadTahorTab = () => {
  const [subTab, setSubTab] = useState('usulan');
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const { toast } = useToast();
  const pgUpload = usePagination(10);
  useEffect(() => { pgUpload.reset(); }, [data]);

  const subTabs = [{ id:'usulan', label:'Upload Usulan Tahor' }, { id:'sidang', label:'Upload Sidang Tahor' }, { id:'setmilpres', label:'Upload Setmilpres' }];

  const handleUpload = async () => {
    if (!file) { toast('Pilih file terlebih dahulu', 'error'); return; }
    try {
      await tahorAPI.createUpload({ tipe: subTab, file_nama: file.name });
      toast('Upload berhasil');
      tahorAPI.getUpload(`?tipe=${subTab}`).then(r => setData(r.data || [])).catch(() => {});
    } catch(e) { toast(e.message, 'error'); }
  };

  useEffect(() => {
    tahorAPI.getUpload(`?tipe=${subTab}`).then(r => setData(r.data || [])).catch(() => {});
  }, [subTab]);

  return (
    <>
      <div style={{ display:'flex',gap:0,marginBottom:16,borderBottom:'1px solid #E5E7EB' }}>
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            style={{ padding:'8px 16px',border:'none',background:'none',cursor:'pointer',fontSize:12,fontWeight:subTab===t.id?700:500,
              color:subTab===t.id?'#7C3AED':'#6B7280',
              borderBottom:subTab===t.id?'2px solid #7C3AED':'2px solid transparent',marginBottom:-1,fontFamily:'inherit' }}>
            {t.label}
          </button>
        ))}
      </div>
      <Card>
        <h3 style={{ margin:'0 0 16px',fontSize:15,fontWeight:700 }}>{subTabs.find(t=>t.id===subTab)?.label}</h3>
        <p style={{ fontSize:12,color:'#9CA3AF',marginBottom:12 }}>*Gunakan format file upload yang telah disediakan</p>
        <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} style={{ marginBottom:12,display:'block' }} />
        <Btn onClick={handleUpload}>Upload</Btn>
      </Card>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>Riwayat Upload ({data.length})</h3>
        {(() => { const { data: paged, total, totalPages, from, to } = pgUpload.paginate(data); return (<>
        <table style={{ width:'100%',borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'#F8FAFC' }}>
            {['No','Tipe','File','Jumlah Data','Tanggal'].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {paged.length === 0
              ? <tr><td colSpan={5} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Belum ada upload</td></tr>
              : paged.map((r,i) => (
                <tr key={i}>
                  <td style={td}>{from + i}</td><td style={td}>{r.tipe}</td><td style={td}>{r.file_nama}</td>
                  <td style={td}>{r.jumlah_data || 0}</td><td style={td}>{r.created_at ? new Date(r.created_at).toLocaleDateString('id-ID') : '-'}</td>
                </tr>
              ))}
          </tbody>
        </table>
        <Pagination page={pgUpload.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgUpload.pageSize} onPageChange={pgUpload.setPage} onPageSizeChange={pgUpload.setPageSize} />
        </>); })()}
      </Card>
    </>
  );
};

// ── BUKU SIDANG TAHOR TAB ─────────────────────────────────
const BukuSidangTab = () => {
  const [subTab, setSubTab] = useState('usulan');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const subTabs = [{ id:'usulan', label:'Nominatif Usulan Tahor' }, { id:'net', label:'Nominatif Net Tahor' }, { id:'penolakan', label:'Nominatif Penolakan Tahor' }, { id:'setmilres', label:'Nominatif Setmilres' }];
  const pgBuku = usePagination(10);
  useEffect(() => { pgBuku.reset(); }, [data]);

  useEffect(() => {
    setLoading(true);
    tahorAPI.getBukuSidang(`?tipe=${subTab}`).then(r => setData(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [subTab]);

  return (
    <>
      <div style={{ display:'flex',gap:0,marginBottom:16,borderBottom:'1px solid #E5E7EB' }}>
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            style={{ padding:'8px 16px',border:'none',background:'none',cursor:'pointer',fontSize:12,fontWeight:subTab===t.id?700:500,
              color:subTab===t.id?'#7C3AED':'#6B7280',
              borderBottom:subTab===t.id?'2px solid #7C3AED':'2px solid transparent',marginBottom:-1,fontFamily:'inherit' }}>
            {t.label}
          </button>
        ))}
      </div>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>{subTabs.find(t=>t.id===subTab)?.label} ({data.length})</h3>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = pgBuku.paginate(data); return (<>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC' }}>
              {['NRP','Nama','Pangkat','Tanda Kehormatan','Kategori','Status'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={6} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Tidak ada data</td></tr>
                : paged.map((r,i) => (
                  <tr key={i}>
                    <td style={td}>{r.nrp}</td><td style={td}>{r.nama}</td><td style={td}>{r.pangkat}</td>
                    <td style={td}>{r.nama_tanda}</td><td style={td}>{r.kategori}</td><td style={td}><StatusBadge status={r.status}/></td>
                  </tr>
                ))}
            </tbody>
          </table>
          <Pagination page={pgBuku.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgBuku.pageSize} onPageChange={pgBuku.setPage} onPageSizeChange={pgBuku.setPageSize} />
          </>); })()
        )}
      </Card>
    </>
  );
};

// ── INPUT TMT PEMAKAIAN TAB ────────────────────────────────
const TmtTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nrp:'', tmt_pemakaian:'', no_kep:'', tgl_kep:'' });
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selPers, setSelPers] = useState(null);
  const { toast } = useToast();
  const pgTmt = usePagination(10);
  useEffect(() => { pgTmt.reset(); }, [data]);

  const load = async () => {
    setLoading(true);
    try { const r = await tahorAPI.getTmt(); setData(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nrp || !form.tmt_pemakaian) { toast('NRP dan TMT Pemakaian wajib diisi', 'error'); return; }
    try {
      if (editId) { await tahorAPI.updateTmt(editId, form); toast('TMT berhasil diubah'); setEditId(null); }
      else { await tahorAPI.createTmt(form); toast('TMT berhasil disimpan'); }
      setForm({ nrp:'', tmt_pemakaian:'', no_kep:'', tgl_kep:'' });
      setSelPers(null);
      load();
    } catch(e) { toast(e.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ margin:'0 0 16px',fontSize:15,fontWeight:700 }}>{editId ? 'Edit' : 'Input'} TMT Pemakaian Tahor</h3>
        <form onSubmit={handleSubmit}>
          <div style={rowStyle}>
            <Field label="NRP Personel" required><NrpLookup value={selPers} onSelect={(p) => { setSelPers(p); setForm(f=>({...f,nrp:p.nrp})); }} onClear={() => { setSelPers(null); setForm(f=>({...f,nrp:''})); }} disabled={!!editId} /></Field>
            <Field label="TMT Pemakaian *"><Input type="date" value={form.tmt_pemakaian} onChange={e => setForm(f=>({...f,tmt_pemakaian:e.target.value}))}/></Field>
          </div>
          <div style={rowStyle}>
            <Field label="No. Keputusan"><Input value={form.no_kep} onChange={e => setForm(f=>({...f,no_kep:e.target.value}))} placeholder="Nomor SK"/></Field>
            <Field label="Tanggal Keputusan"><Input type="date" value={form.tgl_kep} onChange={e => setForm(f=>({...f,tgl_kep:e.target.value}))}/></Field>
          </div>
          <div style={{ display:'flex',gap:8 }}>
            <Btn>{editId ? 'Update' : 'Simpan'}</Btn>
            {editId && <Btn onClick={() => { setEditId(null); setForm({ nrp:'',tmt_pemakaian:'',no_kep:'',tgl_kep:'' }); }}>Batal</Btn>}
          </div>
        </form>
      </Card>
      <Card>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
          <h3 style={{ margin:0,fontSize:15,fontWeight:700 }}>Data TMT Pemakaian ({data.length})</h3>
          <div style={{ display:'flex',gap:8 }}>
            {selected.length > 0 && (
              <Btn variant="success" onClick={async() => {
                try { for(const id of selected) await tahorAPI.distribusiTahor(id); toast('Distribusi berhasil'); setSelected([]); }
                catch(e) { toast(e.message,'error'); }
              }}>Distribusi Tahor ({selected.length})</Btn>
            )}
          </div>
        </div>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = pgTmt.paginate(data); return (<>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC' }}>
              {['','NRP','Nama','Pangkat','TMT Pemakaian','No. Kep','Aksi'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={7} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Belum ada data TMT</td></tr>
                : paged.map((r,i) => (
                  <tr key={i}>
                    <td style={td}><input type="checkbox" checked={selected.includes(r.id)} onChange={e => setSelected(s => e.target.checked ? [...s,r.id] : s.filter(x=>x!==r.id))}/></td>
                    <td style={td}>{r.nrp}</td><td style={td}>{r.nama}</td><td style={td}>{r.pangkat}</td>
                    <td style={td}>{r.tmt_pemakaian ? new Date(r.tmt_pemakaian).toLocaleDateString('id-ID') : '-'}</td>
                    <td style={td}>{r.no_kep || '-'}</td>
                    <td style={td}>
                      <Btn variant="secondary" onClick={() => { setEditId(r.id); setForm({ nrp:r.nrp,tmt_pemakaian:r.tmt_pemakaian||'',no_kep:r.no_kep||'',tgl_kep:r.tgl_kep||'' }); }}>Edit</Btn>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <Pagination page={pgTmt.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgTmt.pageSize} onPageChange={pgTmt.setPage} onPageSizeChange={pgTmt.setPageSize} />
          </>); })()
        )}
      </Card>
    </>
  );
};

// ── HASIL SIDANG TAHOR TAB ────────────────────────────────
const HasilSidangTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ tahun:'' });
  const pgHasil = usePagination(10);
  useEffect(() => { pgHasil.reset(); }, [data]);

  const load = async () => {
    setLoading(true);
    const qs = filter.tahun ? `?tahun=${filter.tahun}` : '';
    try { const r = await tahorAPI.getHasilSidang(qs); setData(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <>
      <Card>
        <div style={{ display:'flex',gap:12,alignItems:'flex-end',marginBottom:0 }}>
          <Field label="Filter Tahun"><Input type="number" value={filter.tahun} onChange={e => setFilter(f=>({...f,tahun:e.target.value}))} placeholder={new Date().getFullYear()} style={{ width:180 }}/></Field>
          <Btn onClick={load}>Filter</Btn>
        </div>
      </Card>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>Hasil Sidang Tahor ({data.length})</h3>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = pgHasil.paginate(data); return (<>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC' }}>
              {['NRP','Nama','Pangkat','Tanda Kehormatan','Kategori','Tingkat','Hasil Sidang','Status'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={8} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Belum ada hasil sidang</td></tr>
                : paged.map((r,i) => (
                  <tr key={i}>
                    <td style={td}>{r.nrp}</td><td style={td}>{r.nama}</td><td style={td}>{r.pangkat}</td>
                    <td style={td}>{r.nama_tanda}</td><td style={td}>{r.kategori}</td><td style={td}>{r.tingkat}</td>
                    <td style={td}><span style={{ padding:'3px 8px',borderRadius:4,fontSize:11,fontWeight:700,
                      background:r.hasil==='DISETUJUI'?'#DCFCE7':r.hasil==='DITOLAK'?'#FEE2E2':'#FFFBEB',
                      color:r.hasil==='DISETUJUI'?'#166534':r.hasil==='DITOLAK'?'#991B1B':'#92400E' }}>{r.hasil || 'PROSES'}</span></td>
                    <td style={td}><StatusBadge status={r.status}/></td>
                  </tr>
                ))}
            </tbody>
          </table>
          <Pagination page={pgHasil.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgHasil.pageSize} onPageChange={pgHasil.setPage} onPageSizeChange={pgHasil.setPageSize} />
          </>); })()
        )}
      </Card>
    </>
  );
};

// ── NOMINATIF USULAN TAHOR TAB ────────────────────────────
const NominatifUsulanTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ kategori:'', pangkat:'' });
  const pgNominatif = usePagination(10);
  useEffect(() => { pgNominatif.reset(); }, [data]);

  const load = async () => {
    setLoading(true);
    const parts = [];
    if (filter.kategori) parts.push(`kategori=${filter.kategori}`);
    if (filter.pangkat)  parts.push(`pangkat=${filter.pangkat}`);
    const qs = parts.length ? `?${parts.join('&')}` : '';
    try { const r = await tahorAPI.getNominatif(qs); setData(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>Filter Nominatif Usulan Tahor</h3>
        <div style={rowStyle}>
          <Field label="Kategori"><Select value={filter.kategori} onChange={e => setFilter(f=>({...f,kategori:e.target.value}))}
            options={[{ value:'', label:'Semua Kategori' }, { value:'BINTANG', label:'Bintang' }, { value:'SATYA_LENCANA', label:'Satya Lencana' }, { value:'TANDA_JASA', label:'Tanda Jasa' }]} /></Field>
          <Field label="Pangkat"><Select value={filter.pangkat} onChange={e => setFilter(f=>({...f,pangkat:e.target.value}))}
            options={[{ value:'', label:'Semua Pangkat' }, { value:'JENDERAL', label:'Jenderal' }, { value:'LETJEN', label:'Letjen' }, { value:'MAYJEN', label:'Mayjen' }, { value:'BRIGJEN', label:'Brigjen' }, { value:'KOLONEL', label:'Kolonel' }]} /></Field>
        </div>
        <Btn onClick={load}>Filter</Btn>
      </Card>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>Nominatif Usulan Tahor ({data.length})</h3>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = pgNominatif.paginate(data); return (<>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC' }}>
              {['No','NRP','Nama','Pangkat','Tanda Kehormatan','Kategori','Tingkat','No. Persetujuan','Status'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={9} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Tidak ada nominatif</td></tr>
                : paged.map((r,i) => (
                  <tr key={i}>
                    <td style={td}>{from + i}</td><td style={td}>{r.nrp}</td><td style={td}>{r.nama}</td><td style={td}>{r.pangkat}</td>
                    <td style={td}>{r.nama_tanda}</td><td style={td}>{r.kategori}</td><td style={td}>{r.tingkat}</td>
                    <td style={td}>{r.no_persetujuan || '-'}</td><td style={td}><StatusBadge status={r.status}/></td>
                  </tr>
                ))}
            </tbody>
          </table>
          <Pagination page={pgNominatif.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgNominatif.pageSize} onPageChange={pgNominatif.setPage} onPageSizeChange={pgNominatif.setPageSize} />
          </>); })()
        )}
      </Card>
    </>
  );
};

// ── MAIN ────────────────────────────────────────────────────
export default function TahorPage() {
  const [activeTab, setActiveTab] = useState('upload');
  const tabs = [
    { id: 'upload',     label: 'Upload Tahor' },
    { id: 'buku_sidang',label: 'Buku Sidang Tahor' },
    { id: 'tmt',        label: 'Input TMT Pemakaian' },
    { id: 'hasil',      label: 'Hasil Sidang Tahor' },
    { id: 'nominatif',  label: 'Nominatif Usulan Tahor' },
    { id: 'master',     label: 'Master Tanda Kehormatan' },
    { id: 'riwayat',    label: 'Riwayat Tahor' },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', margin: 0 }}>TAHOR</h1>
        <p style={{ color: '#64748B', fontSize: 13, margin: '4px 0 0' }}>Tanda Kehormatan Personel TNI AD</p>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #E2E8F0', flexWrap:'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              color: activeTab === t.id ? '#2563EB' : '#64748B',
              borderBottom: activeTab === t.id ? '2px solid #2563EB' : '2px solid transparent', marginBottom: -2, whiteSpace:'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'upload'      && <UploadTahorTab />}
      {activeTab === 'buku_sidang' && <BukuSidangTab />}
      {activeTab === 'tmt'         && <TmtTab />}
      {activeTab === 'hasil'       && <HasilSidangTab />}
      {activeTab === 'nominatif'   && <NominatifUsulanTab />}
      {activeTab === 'master'      && <MasterTab />}
      {activeTab === 'riwayat'     && <RiwayatTab />}
    </div>
  );
}
